/**
 * Iter FD · Partner Onboarding Wizard (admin-facing).
 *
 * 3-step compact wizard that composes the Iter FA/FB/FB.5/FC framework
 * endpoints into an admin experience for recording an integration
 * decision on an existing partner_organization.
 *
 *   Step 1 · Discover  — pick integration type + optional context
 *   Step 2 · Recommend — call engine, render explainability panels
 *   Step 3 · Review    — edit overrides, record versioned decision
 *
 * Guardrails (per user directive):
 *   - Admin-facing only. Public application flow untouched.
 *   - Recommendations remain advisory + editable.
 *   - Decision record is persisted (Iter FC). Permission grants are NOT
 *     applied — the "apply permissions" button is disabled and labelled
 *     "coming in a later iteration".
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, CheckCircle2, Loader2, Search,
  ShieldAlert, ShieldCheck, Save, Lightbulb, Activity,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import "./PartnerOnboardingWizard.css";

const STEPS = [
  { key: "discover",  label: "Discover"  },
  { key: "recommend", label: "Recommend" },
  { key: "review",    label: "Review"    },
];

const CATEGORY_ORDER = ["provider", "community", "government", "vendor"];
const CATEGORY_LABEL = {
  provider: "Providers",
  community: "Community",
  government: "Government",
  vendor: "Vendors",
};

const CONFIDENCE_TONE = { high: "ok", medium: "warn", low: "crit" };

export default function PartnerOnboardingWizard() {
  const { partnerId } = useParams();
  const nav = useNavigate();

  const [step, setStep] = useState(0);
  const [types, setTypes] = useState([]);
  const [partner, setPartner] = useState(null);
  const [typeQuery, setTypeQuery] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [context, setContext] = useState({
    declared_use_cases: [],
    declared_data_direction: "",
    declared_expected_traffic: "",
    existing_profile_key: "",
  });
  const [recommendation, setRecommendation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [overrides, setOverrides] = useState({});
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  // Re-entrancy guard — React 18 StrictMode double-invokes callbacks in dev,
  // and a fast double-click could also fire two submits before `busy` commits.
  // We use a ref so the guard is synchronous.
  const submittingRef = useRef(false);

  // ── Initial load: partner header + integration-type catalog ────────
  useEffect(() => {
    (async () => {
      try {
        const [p, catalog] = await Promise.all([
          api.get(`/admin/partners/${partnerId}`),
          api.get("/admin/partners/integration-types"),
        ]);
        setPartner(p.data.partner || p.data);
        setTypes(catalog.data.integration_types || []);
      } catch (e) {
        toast.error(formatApiError(e, "Could not load partner or catalog"));
      } finally {
        setLoading(false);
      }
    })();
  }, [partnerId]);

  const filteredTypes = useMemo(() => {
    const q = typeQuery.trim().toLowerCase();
    if (!q) return types;
    return types.filter((t) =>
      t.label.toLowerCase().includes(q)
      || t.description.toLowerCase().includes(q)
      || t.key.toLowerCase().includes(q),
    );
  }, [types, typeQuery]);

  const typesByCategory = useMemo(() => {
    const out = {};
    for (const t of filteredTypes) {
      (out[t.category] ||= []).push(t);
    }
    return out;
  }, [filteredTypes]);

  // ── Step 2: fetch recommendation ───────────────────────────────────
  const fetchRecommendation = useCallback(async () => {
    if (!selectedType) return;
    setBusy(true);
    try {
      const orgContext = {};
      if (context.declared_use_cases.length)
        orgContext.declared_use_cases = context.declared_use_cases;
      if (context.declared_data_direction)
        orgContext.declared_data_direction = context.declared_data_direction;
      if (context.declared_expected_traffic)
        orgContext.declared_expected_traffic = context.declared_expected_traffic;
      if (context.existing_profile_key)
        orgContext.existing_profile_key = context.existing_profile_key;
      if (partner?.name) orgContext.organization_name = partner.name;

      const resp = await api.post("/admin/partners/recommendations", {
        integration_type: selectedType,
        organization_context: Object.keys(orgContext).length ? orgContext : null,
        include_explanation: true,
        include_confidence: true,
        include_trace: true,
        include_alternatives: true,
      });
      setRecommendation(resp.data);
      // Seed overrides with the recommendation defaults so the Review
      // step can show accepted-as-is state until the admin changes something.
      setOverrides({
        primary_auth_method: resp.data.recommended.primary_auth_method,
        profile_key: resp.data.recommended.profile.key,
      });
      setStep(1);
    } catch (e) {
      toast.error(formatApiError(e, "Could not fetch recommendation"));
    } finally {
      setBusy(false);
    }
  }, [selectedType, context, partner]);

  // ── Step 3: persist decision ────────────────────────────────────────
  const submitDecision = useCallback(async () => {
    if (reason.trim().length < 20) {
      toast.error("Audit reason must be at least 20 characters.");
      return;
    }
    if (submittingRef.current) return;
    submittingRef.current = true;
    setBusy(true);
    try {
      // Build overrides payload — only send fields that actually differ
      // from the recommendation.
      const diff = {};
      const rec = recommendation.recommended;
      if (overrides.profile_key && overrides.profile_key !== rec.profile.key)
        diff.profile_key = overrides.profile_key;
      if (overrides.primary_auth_method
            && overrides.primary_auth_method !== rec.primary_auth_method)
        diff.primary_auth_method = overrides.primary_auth_method;
      if (notes.trim()) diff.notes = notes.trim();

      const body = {
        integration_type: selectedType,
        reason: reason.trim(),
      };
      if (Object.keys(diff).length) body.overrides = diff;

      const resp = await api.post(
        `/admin/partners/${partnerId}/integration-decision`,
        body,
      );
      toast.success(
        resp.data.decision.accepted_as_is
          ? "Decision recorded (accepted as recommended)."
          : "Decision recorded with overrides.",
      );
      nav(`/admin/partners`);
    } catch (e) {
      toast.error(formatApiError(e, "Could not record decision"));
    } finally {
      setBusy(false);
      submittingRef.current = false;
    }
  }, [reason, notes, overrides, recommendation, selectedType, partnerId, nav]);

  const selectedTypeMeta = useMemo(
    () => types.find((t) => t.key === selectedType),
    [types, selectedType],
  );

  if (loading) {
    return (
      <div className="fd-wiz__loading" data-testid="fd-wizard-loading">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading wizard…
      </div>
    );
  }

  return (
    <div className="fd-wiz" data-testid="fd-wizard">
      <header className="fd-wiz__hero">
        <div className="fd-wiz__eyebrow">
          <Lightbulb className="w-3.5 h-3.5" /> Partner Integration Framework
        </div>
        <h1>Onboarding Wizard</h1>
        <p className="fd-wiz__sub">
          {partner?.name ? <>Recording an integration decision for <strong>{partner.name}</strong>. </> : null}
          Advisory recommendations. No permissions granted by this flow.
        </p>

        <ol className="fd-wiz__steps" aria-label="wizard progress">
          {STEPS.map((s, i) => (
            <li
              key={s.key}
              className={`fd-wiz__step ${i === step ? "fd-wiz__step--active" : ""} ${i < step ? "fd-wiz__step--done" : ""}`}
              data-testid={`fd-wizard-progress-${s.key}`}
            >
              <span className="fd-wiz__step-num">{i + 1}</span>
              <span className="fd-wiz__step-label">{s.label}</span>
            </li>
          ))}
        </ol>
      </header>

      {step === 0 && (
        <StepDiscover
          types={filteredTypes}
          typesByCategory={typesByCategory}
          typeQuery={typeQuery} onTypeQuery={setTypeQuery}
          selectedType={selectedType} onSelect={setSelectedType}
          context={context} onContext={setContext}
          onNext={fetchRecommendation}
          busy={busy}
        />
      )}

      {step === 1 && recommendation && (
        <StepRecommend
          rec={recommendation}
          onBack={() => setStep(0)}
          onNext={() => setStep(2)}
          onPickAlternative={(alt) => {
            setSelectedType(alt);
            setRecommendation(null);
            setStep(0);
          }}
        />
      )}

      {step === 2 && recommendation && (
        <StepReview
          rec={recommendation}
          overrides={overrides} onOverrides={setOverrides}
          reason={reason} onReason={setReason}
          notes={notes} onNotes={setNotes}
          busy={busy}
          onBack={() => setStep(1)}
          onSubmit={submitDecision}
          selectedTypeMeta={selectedTypeMeta}
        />
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Step 1 · Discover
// ────────────────────────────────────────────────────────────────────────
function StepDiscover({
  types, typesByCategory, typeQuery, onTypeQuery,
  selectedType, onSelect, context, onContext,
  onNext, busy,
}) {
  return (
    <section className="fd-wiz__card" data-testid="fd-step-discover">
      <h2>Step 1 · Discover</h2>
      <p className="fd-wiz__card-sub">
        Pick the integration type that best matches how this partner will
        connect. You can refine with optional context below.
      </p>

      <div className="fd-wiz__search">
        <Search className="w-4 h-4" />
        <input
          type="text"
          placeholder="Search integration types…"
          value={typeQuery}
          onChange={(e) => onTypeQuery(e.target.value)}
          data-testid="fd-discover-search"
        />
      </div>

      {CATEGORY_ORDER.map((cat) => {
        const rows = typesByCategory[cat] || [];
        if (!rows.length) return null;
        return (
          <div key={cat} className="fd-wiz__cat-block"
                data-testid={`fd-discover-category-${cat}`}>
            <h3 className="fd-wiz__cat-title">{CATEGORY_LABEL[cat] || cat}</h3>
            <div className="fd-wiz__grid">
              {rows.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  className={`fd-wiz__type ${selectedType === t.key ? "fd-wiz__type--sel" : ""}`}
                  onClick={() => onSelect(t.key)}
                  data-testid={`fd-discover-type-${t.key}`}
                >
                  <div className="fd-wiz__type-head">
                    <span className="fd-wiz__type-label">{t.label}</span>
                    {selectedType === t.key && <CheckCircle2 className="w-4 h-4" />}
                  </div>
                  <div className="fd-wiz__type-desc">{t.description}</div>
                  <div className="fd-wiz__type-tags">
                    <span className="fd-wiz__tag">{t.data_direction}</span>
                    <span className="fd-wiz__tag">{t.expected_traffic}</span>
                    <span className="fd-wiz__tag">v{t.version}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        );
      })}

      {types.length === 0 && (
        <div className="fd-wiz__empty" data-testid="fd-discover-empty">
          No integration types match this search.
        </div>
      )}

      <details className="fd-wiz__context" data-testid="fd-discover-context">
        <summary>Optional context (helps the recommendation engine)</summary>
        <div className="fd-wiz__context-grid">
          <label>
            Data direction
            <select
              value={context.declared_data_direction}
              onChange={(e) => onContext({ ...context, declared_data_direction: e.target.value })}
              data-testid="fd-discover-direction">
              <option value="">— unspecified —</option>
              <option value="inbound">Inbound</option>
              <option value="outbound">Outbound</option>
              <option value="bidirectional">Bidirectional</option>
            </select>
          </label>
          <label>
            Expected traffic
            <select
              value={context.declared_expected_traffic}
              onChange={(e) => onContext({ ...context, declared_expected_traffic: e.target.value })}
              data-testid="fd-discover-traffic">
              <option value="">— unspecified —</option>
              <option value="none">None</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="realtime">Realtime</option>
            </select>
          </label>
          <label>
            Existing permission profile
            <input
              type="text"
              value={context.existing_profile_key}
              onChange={(e) => onContext({ ...context, existing_profile_key: e.target.value })}
              placeholder="e.g. read_only"
              data-testid="fd-discover-existing-profile"
            />
          </label>
        </div>
      </details>

      <footer className="fd-wiz__actions">
        <span className="fd-wiz__sel-hint" data-testid="fd-discover-selected">
          {selectedType ? `Selected: ${selectedType}` : "No integration type selected yet"}
        </span>
        <button
          type="button" className="fd-wiz__primary"
          disabled={!selectedType || busy}
          onClick={onNext}
          data-testid="fd-discover-next">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
          Continue to Recommend <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </footer>
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Step 2 · Recommend
// ────────────────────────────────────────────────────────────────────────
function StepRecommend({ rec, onBack, onNext, onPickAlternative }) {
  const conf = rec.confidence || {};
  const tone = CONFIDENCE_TONE[conf.level] || "warn";
  const expl = rec.explanation || {};

  return (
    <section className="fd-wiz__card" data-testid="fd-step-recommend">
      <h2>Step 2 · Recommendation</h2>
      <p className="fd-wiz__card-sub">
        Advisory. You can edit everything in the next step before it becomes
        a decision record.
      </p>

      <div className={`fd-wiz__confidence fd-wiz__confidence--${tone}`}
            data-testid="fd-recommend-confidence">
        <div>
          <div className="fd-wiz__conf-label">Confidence</div>
          <div className="fd-wiz__conf-level">{conf.level || "—"}</div>
        </div>
        <div className="fd-wiz__conf-factors">
          {(conf.factors || []).map((f, i) => (
            <span key={i} className="fd-wiz__conf-factor">
              {f.factor} <em>{f.delta}</em>
            </span>
          ))}
        </div>
      </div>

      <div className="fd-wiz__panel" data-testid="fd-recommend-profile">
        <h3>Profile</h3>
        <div className="fd-wiz__panel-body">
          <strong>{rec.recommended.profile.label}</strong> ({rec.recommended.profile.key})
          <p className="fd-wiz__panel-reason">{expl.profile?.reason}</p>
        </div>
      </div>

      <div className="fd-wiz__panel" data-testid="fd-recommend-scopes">
        <h3>Scopes ({rec.recommended.scopes.length})</h3>
        <ul className="fd-wiz__scope-list">
          {rec.recommended.scopes.map((s) => (
            <li key={s.key}>
              <span className={`fd-wiz__risk fd-wiz__risk--${s.risk || "medium"}`}>
                {s.risk || "medium"}
              </span>
              <code>{s.key}</code>
              <span className="fd-wiz__scope-src">{s.source}</span>
            </li>
          ))}
        </ul>
        {rec.recommended.denied_scopes.length > 0 && (
          <div className="fd-wiz__denied">
            <strong>Denied:</strong> {rec.recommended.denied_scopes.join(", ")}
          </div>
        )}
      </div>

      <div className="fd-wiz__panel" data-testid="fd-recommend-auth">
        <h3>Authentication</h3>
        <div className="fd-wiz__auth-list">
          {rec.recommended.auth_methods.map((a) => (
            <span key={a.key}
                    className={`fd-wiz__auth ${a.primary ? "fd-wiz__auth--primary" : ""}`}>
              {a.label || a.key} {a.primary && "· primary"}
            </span>
          ))}
        </div>
        <p className="fd-wiz__panel-reason">{expl.authentication?.reason}</p>
      </div>

      <details className="fd-wiz__panel" data-testid="fd-recommend-webhooks">
        <summary><h3 style={{display:"inline"}}>Webhooks ({rec.recommended.webhook_subscriptions.length})</h3></summary>
        <ul className="fd-wiz__flat-list">
          {rec.recommended.webhook_subscriptions.map((w) => (
            <li key={w.key}><code>{w.key}</code> — {w.label} <em>({w.compliance_level})</em></li>
          ))}
        </ul>
      </details>

      <details className="fd-wiz__panel" data-testid="fd-recommend-compliance">
        <summary><h3 style={{display:"inline"}}>Compliance ({rec.recommended.compliance_requirements.length})</h3></summary>
        <ul className="fd-wiz__flat-list">
          {rec.recommended.compliance_requirements.map((c) => (
            <li key={c.key}><code>{c.key}</code> — {c.label} <em>({c.risk_level})</em></li>
          ))}
        </ul>
      </details>

      <details className="fd-wiz__panel" data-testid="fd-recommend-docs">
        <summary><h3 style={{display:"inline"}}>Documentation ({rec.recommended.documentation_requirements.length})</h3></summary>
        <ul className="fd-wiz__flat-list">
          {rec.recommended.documentation_requirements.map((d) => (
            <li key={d.key}><code>{d.key}</code> — {d.label}</li>
          ))}
        </ul>
      </details>

      <details className="fd-wiz__panel" data-testid="fd-recommend-trace">
        <summary><h3 style={{display:"inline"}}>Trace</h3></summary>
        <p className="fd-wiz__arrow">{rec.trace?.arrow_form}</p>
        <ol className="fd-wiz__trace-list">
          {(rec.trace?.steps || []).map((s, i) => (
            <li key={i}><strong>{s.step}:</strong> {s.value} <em>({s.detail})</em></li>
          ))}
        </ol>
      </details>

      {rec.alternatives && rec.alternatives.length > 0 && (
        <div className="fd-wiz__alts" data-testid="fd-recommend-alternatives">
          <h3>Alternatives</h3>
          <div className="fd-wiz__alt-grid">
            {rec.alternatives.map((a) => (
              <div key={a.alt_key} className="fd-wiz__alt">
                <div className="fd-wiz__alt-head">{a.label}</div>
                <div className="fd-wiz__alt-reason">{a.reason}</div>
                <button
                  type="button"
                  className="fd-wiz__ghost"
                  onClick={() => onPickAlternative(a.alt_key)}
                  data-testid={`fd-recommend-alt-${a.alt_key}`}>
                  Switch to {a.alt_key}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <footer className="fd-wiz__actions">
        <button type="button" className="fd-wiz__ghost" onClick={onBack}
                 data-testid="fd-recommend-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>
        <button type="button" className="fd-wiz__primary" onClick={onNext}
                 data-testid="fd-recommend-next">
          Continue to Review <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </footer>
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Step 3 · Review
// ────────────────────────────────────────────────────────────────────────
function StepReview({
  rec, overrides, onOverrides, reason, onReason, notes, onNotes,
  busy, onBack, onSubmit, selectedTypeMeta,
}) {
  const rec_ = rec.recommended;
  const authOptions = rec_.auth_methods.map((a) => a.key);
  const overrode =
    (overrides.profile_key && overrides.profile_key !== rec_.profile.key)
    || (overrides.primary_auth_method && overrides.primary_auth_method !== rec_.primary_auth_method)
    || (notes.trim().length > 0);

  return (
    <section className="fd-wiz__card" data-testid="fd-step-review">
      <h2>Step 3 · Review &amp; Record Decision</h2>
      <p className="fd-wiz__card-sub">
        Adjust anything you want to override. Nothing is granted — this
        step only records an immutable decision.
      </p>

      <div className={`fd-wiz__panel fd-wiz__panel--flag ${overrode ? "fd-wiz__panel--warn" : "fd-wiz__panel--ok"}`}
            data-testid="fd-review-accepted-flag">
        {overrode
          ? <>Overrides detected — <strong>accepted_as_is</strong> will be <code>false</code>.</>
          : <>No overrides — <strong>accepted_as_is</strong> will be <code>true</code>.</>}
      </div>

      <div className="fd-wiz__panel">
        <h3>Integration Type</h3>
        <div className="fd-wiz__panel-body">
          <strong>{selectedTypeMeta?.label}</strong> · {selectedTypeMeta?.key}
        </div>
      </div>

      <div className="fd-wiz__panel">
        <h3>Permission Profile</h3>
        <input
          type="text"
          value={overrides.profile_key || ""}
          onChange={(e) => onOverrides({ ...overrides, profile_key: e.target.value })}
          data-testid="fd-review-profile"
        />
        <div className="fd-wiz__panel-hint">
          Recommended: <code>{rec_.profile.key}</code>
        </div>
      </div>

      <div className="fd-wiz__panel">
        <h3>Primary Authentication Method</h3>
        <select
          value={overrides.primary_auth_method || rec_.primary_auth_method}
          onChange={(e) => onOverrides({ ...overrides, primary_auth_method: e.target.value })}
          data-testid="fd-review-auth">
          {authOptions.map((k) => (
            <option key={k} value={k}>{k}</option>
          ))}
        </select>
        <div className="fd-wiz__panel-hint">
          Recommended: <code>{rec_.primary_auth_method}</code>
        </div>
      </div>

      <div className="fd-wiz__panel">
        <h3>Override notes (optional)</h3>
        <textarea
          rows={2} maxLength={2000}
          value={notes}
          onChange={(e) => onNotes(e.target.value)}
          placeholder="Explain why you're overriding (if applicable)…"
          data-testid="fd-review-notes"
        />
      </div>

      <div className="fd-wiz__panel">
        <h3>Audit reason (required, ≥20 chars)</h3>
        <textarea
          rows={3} maxLength={500}
          value={reason}
          onChange={(e) => onReason(e.target.value)}
          placeholder="e.g. Ticket #4821 — recording initial integration decision after security review."
          data-testid="fd-review-reason"
        />
        <div className="fd-wiz__panel-hint" data-testid="fd-review-reason-count">
          {reason.length}/20 characters minimum
        </div>
      </div>

      <footer className="fd-wiz__actions">
        <button type="button" className="fd-wiz__ghost" onClick={onBack}
                 data-testid="fd-review-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>
        <button type="button" className="fd-wiz__primary"
                 disabled={busy || reason.trim().length < 20}
                 onClick={onSubmit}
                 data-testid="fd-review-record">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          Record decision only (no permissions granted)
        </button>
        <button type="button" className="fd-wiz__ghost fd-wiz__ghost--muted"
                 disabled title="Coming in a later iteration."
                 data-testid="fd-review-record-and-apply">
          <ShieldCheck className="w-3.5 h-3.5" />
          Record decision AND apply permissions
        </button>
      </footer>
    </section>
  );
}
